import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, KeyRound, X, Trash2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import GalleryEditor from "../components/admin/GalleryEditor";

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("gallery");
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);

  // Change password modal state
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState("");

  useEffect(() => {
    if (!ready) return;
    if (!user || user.role !== "admin") {
      nav("/admin/login");
      return;
    }
    refreshBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  const refreshBookings = async () => {
    setBookingsLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBookingsLoading(false);
    }
  };

  const removeBooking = async (booking) => {
    if (!window.confirm(`Delete booking from ${booking.name}?`)) return;
    try {
      await api.delete(`/bookings/${booking.id}`);
      toast.success("Booking removed.");
      refreshBookings();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const signOut = () => {
    logout();
    nav("/admin/login");
  };

  const openChangePassword = () => {
    setPwdForm({ current_password: "", new_password: "", confirm: "" });
    setPwdError("");
    setPwdOpen(true);
  };

  const submitChangePassword = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (pwdForm.new_password.length < 8) {
      setPwdError("New password must be at least 8 characters.");
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm) {
      setPwdError("New password and confirmation do not match.");
      return;
    }
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success("Password changed. Please sign in again with your new password.");
      setPwdOpen(false);
      logout();
      nav("/admin/login");
    } catch (err) {
      setPwdError(formatApiError(err));
    } finally {
      setPwdLoading(false);
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--pov-cyan)]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-page">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline">Administration</p>
            <h1 className="font-display font-light text-4xl tracking-tight mt-3">Practice Console</h1>
            <p className="text-white/55 text-sm mt-1">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openChangePassword} className="btn-secondary text-sm" data-testid="admin-change-password"><KeyRound className="w-4 h-4" /> Change Password</button>
            <button onClick={signOut} className="btn-secondary text-sm" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign Out</button>
          </div>
        </div>

        <div className="mt-8 inline-flex rounded-sm overflow-hidden border border-white/10">
          <button onClick={() => setTab("gallery")} className={`px-5 py-3 text-sm transition-colors ${tab === "gallery" ? "bg-white/8 text-white" : "text-white/55 hover:text-white"}`} data-testid="tab-gallery">Gallery Editor</button>
          <button onClick={() => setTab("bookings")} className={`px-5 py-3 text-sm transition-colors ${tab === "bookings" ? "bg-white/8 text-white" : "text-white/55 hover:text-white"}`} data-testid="tab-bookings">Bookings ({bookings.length})</button>
        </div>

        <div className="mt-8">
          {tab === "gallery" ? (
            <GalleryEditor />
          ) : (
            <div className="glass rounded-sm overflow-hidden" data-testid="bookings-list">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-white/50 uppercase tracking-wider text-xs">
                    <tr className="border-b border-white/8">
                      <th className="py-4 px-5">Type</th>
                      <th className="py-4 px-5">Name</th>
                      <th className="py-4 px-5">Email</th>
                      <th className="py-4 px-5">Field</th>
                      <th className="py-4 px-5">Date</th>
                      <th className="py-4 px-5">Amount</th>
                      <th className="py-4 px-5">Status</th>
                      <th className="py-4 px-5">Submitted</th>
                      <th className="py-4 px-5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingsLoading ? (
                      <tr><td colSpan={9} className="py-10 px-5 text-white/55 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                    ) : bookings.length === 0 ? (
                      <tr><td colSpan={9} className="py-10 px-5 text-white/55 text-center">No bookings yet.</td></tr>
                    ) : bookings.map(b => (
                      <tr key={b.id} className="border-b border-white/5 hover:bg-white/3" data-testid={`booking-row-${b.id}`}>
                        <td className="py-4 px-5">
                          <span className={`px-2 py-1 text-xs rounded-sm ${b.type === "premium" ? "bg-[var(--pov-coral)]/20 text-[var(--pov-coral)]" : "bg-[var(--pov-cyan)]/15 text-[var(--pov-cyan)]"}`}>
                            {b.type}
                          </span>
                        </td>
                        <td className="py-4 px-5">{b.name}</td>
                        <td className="py-4 px-5 text-white/65">{b.email}</td>
                        <td className="py-4 px-5">{b.field}</td>
                        <td className="py-4 px-5 text-white/65">{b.preferred_date || "—"}</td>
                        <td className="py-4 px-5">{b.amount ? `$${b.amount.toFixed(2)}` : "—"}</td>
                        <td className="py-4 px-5">{b.status}{b.payment_status ? ` · ${b.payment_status}` : ""}</td>
                        <td className="py-4 px-5 text-white/55 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                        <td className="py-4 px-5">
                          <button
                            onClick={() => removeBooking(b)}
                            className="w-8 h-8 grid place-items-center rounded-sm border border-white/10 hover:border-[var(--pov-coral)] text-white/60 hover:text-[var(--pov-coral)]"
                            aria-label="Delete booking"
                            data-testid={`delete-booking-${b.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />

      {/* Change Password Modal */}
      {pwdOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#030712]/80 backdrop-blur-sm"
          onClick={() => !pwdLoading && setPwdOpen(false)}
          data-testid="change-password-modal"
        >
          <div
            className="glass-strong rounded-sm w-full max-w-md p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPwdOpen(false)}
              className="absolute top-4 right-4 text-white/55 hover:text-white"
              aria-label="Close"
              data-testid="change-password-close"
            >
              <X className="w-5 h-5" />
            </button>
            <p className="overline">Security</p>
            <h2 className="font-display text-2xl mt-2">Change Password</h2>
            <p className="text-sm text-white/55 mt-2">
              Set a new password for {user?.email}. You'll be signed out after the change.
            </p>
            <form onSubmit={submitChangePassword} className="mt-6 space-y-3" data-testid="change-password-form">
              <input
                type="password"
                className="input"
                placeholder="Current password"
                required
                autoComplete="current-password"
                value={pwdForm.current_password}
                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                data-testid="pwd-current"
              />
              <input
                type="password"
                className="input"
                placeholder="New password (min 8 characters)"
                required
                minLength={8}
                autoComplete="new-password"
                value={pwdForm.new_password}
                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                data-testid="pwd-new"
              />
              <input
                type="password"
                className="input"
                placeholder="Confirm new password"
                required
                minLength={8}
                autoComplete="new-password"
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                data-testid="pwd-confirm"
              />
              {pwdError ? (
                <p className="text-sm text-[var(--pov-coral)]" data-testid="pwd-error">{pwdError}</p>
              ) : null}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setPwdOpen(false)}
                  className="btn-secondary text-sm"
                  disabled={pwdLoading}
                  data-testid="pwd-cancel"
                >
                  Cancel
                </button>
                <button className="btn-primary text-sm" disabled={pwdLoading} data-testid="pwd-submit">
                  {pwdLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
